/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { TrackingData } from 'js/lib/retracked';

import {
  Button,
  ButtonGroup,
  CheckboxCard,
  CheckboxGroup,
  Dialog,
  LoadingSection,
  breakpoints,
} from '@coursera/cds-core';

import UnknownAvailabilityDescription from 'bundles/degree-home/components/v3-dpm/components/CourseCard/UnknownAvailabilityDescription';
import ErrorCard from 'bundles/degree-home/components/v3-dpm/components/ErrorCard';
import NoCoursesCard from 'bundles/degree-home/components/v3-dpm/components/PlanningTool/NoCoursesCard';
import type { RequirementTypeStore } from 'bundles/degree-home/components/v3-dpm/context/LearnerPlanStoreContext';
import type { UseCourseOfferings_CourseOfferingFragment as SegmentCourseOffering } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/useCourseOfferings';
import { dedupSegCourses } from 'bundles/degree-home/components/v3-dpm/lib/termsUtils';
import useCourseOfferings from 'bundles/degree-home/components/v3-dpm/lib/useCourseOfferings';
import CreditCount from 'bundles/program-loggedin-shared/components/CreditCount';
import { METADATA_SEPARATOR } from 'bundles/program-loggedin-shared/components/Metadata';
import RequirementType from 'bundles/program-loggedin-shared/components/RequirementType';

import _t from 'i18n!nls/degree-home';

const styles = {
  fullWidthMobileButtons: css`
    ${breakpoints.only('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
};

type Props = {
  currentEstimatedTermId: string;
  selectedSegmentCourseIds: Set<string>;
  plannableCourseIdsSet: Set<string>;
  updateMutationLoading: boolean;
  termName: string;
  requirementTypeData: RequirementTypeStore | undefined;
  onCheckboxGroupSelectionsChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  addCoursesHandler: (trackingData: TrackingData) => void;
  closeDialog: () => void;
};

const AddCoursesDialog = ({
  currentEstimatedTermId,
  selectedSegmentCourseIds,
  plannableCourseIdsSet,
  updateMutationLoading,
  termName,
  requirementTypeData,
  onCheckboxGroupSelectionsChange,
  addCoursesHandler,
  closeDialog,
}: Props) => {
  const { data, loading, error } = useCourseOfferings(currentEstimatedTermId);
  const segmentCourseOfferings = data?.Degree?.queryById?.courseOfferingsPaginated?.elements ?? [];

  const segmentCourseOfferingsToDisplay = segmentCourseOfferings.filter((courseOffering) =>
    plannableCourseIdsSet.has(courseOffering.id)
  );

  // course dedup based on the segmentCourseId
  const dedupedSegCourseOfferings = dedupSegCourses(segmentCourseOfferingsToDisplay);

  const segmentCourseIdToCourseOfferingMap: Record<string, SegmentCourseOffering> = dedupedSegCourseOfferings.reduce(
    (currMap, course) => ({ ...currMap, [course.id]: course }),
    {}
  );

  const trackingData = {
    termId: currentEstimatedTermId,
    courses: [...selectedSegmentCourseIds]
      .map((segmentCourseId) => segmentCourseIdToCourseOfferingMap[segmentCourseId])
      .filter((course) => !!course)
      .map((course) => {
        const courseName = course.course.courseName;

        const courseCreditValue = course.creditValue;
        const isElective = requirementTypeData?.[course.id]?.isElective;
        let requirementType = '';
        if (isElective !== undefined) {
          requirementType = isElective ? 'Elective' : 'Required';
        }
        return { courseName, courseCreditValue, requirementType };
      }),
  };

  const dialogCheckboxGroup = (
    <CheckboxGroup
      label={_t('Choose from the following courses that will be available on #{termName}:', {
        termName,
      })}
      onChange={onCheckboxGroupSelectionsChange}
    >
      {dedupedSegCourseOfferings.map((segmentCourse) => {
        // fail gracefully if requirementTypeData is undefined
        const isElective = requirementTypeData?.[segmentCourse.id]?.isElective;
        return (
          <CheckboxCard
            key={segmentCourse.id}
            label={_t('#{courseCode} - #{courseName}', {
              courseCode: segmentCourse.course.courseCode,
              courseName: segmentCourse.course.courseName,
            })}
            supportText={
              <React.Fragment>
                {isElective !== undefined && (
                  <React.Fragment>
                    <RequirementType isElective={isElective} key="RequirementType" />
                    {METADATA_SEPARATOR}
                  </React.Fragment>
                )}
                <CreditCount credits={segmentCourse.creditValue} />
                {segmentCourse.availabilityUnconfirmed && <UnknownAvailabilityDescription withMetadataSeparator />}
              </React.Fragment>
            }
            value={segmentCourse.id}
          />
        );
      })}
    </CheckboxGroup>
  );

  const isCourseSelectionEmpty = dedupedSegCourseOfferings.length === 0;
  const dialogContent = isCourseSelectionEmpty ? (
    <NoCoursesCard
      description={_t('All courses offered this term have been either completed or added to your graduation plan.')}
    />
  ) : (
    dialogCheckboxGroup
  );

  const dialogActions = isCourseSelectionEmpty ? (
    <Button css={styles.fullWidthMobileButtons} onClick={closeDialog}>
      {_t('OK')}
    </Button>
  ) : (
    <ButtonGroup
      primaryButtonProps={{
        children: _t('Add courses'),
        disabled: selectedSegmentCourseIds.size === 0,
        loading: updateMutationLoading,
        onClick: () => addCoursesHandler(trackingData),
      }}
      alternateButtonProps={{ children: _t('Cancel'), variant: 'secondary', onClick: closeDialog }}
    />
  );

  return (
    <Dialog open color="darkBlue" sticky="actions" onClose={closeDialog}>
      <Dialog.HeadingGroup supportText={_t('Add course to academic term')}>{_t('Add courses')}</Dialog.HeadingGroup>
      <Dialog.Content>
        {loading && <LoadingSection label={_t('Fetching data...')} />}
        {error && <ErrorCard hideBorder />}
        {data && !error && dialogContent}
      </Dialog.Content>
      <Dialog.Actions>{loading || error ? null : dialogActions}</Dialog.Actions>
    </Dialog>
  );
};

export default AddCoursesDialog;
